import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, Sparkles, Home, FileText, Gift, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

const SEEN_KEY = "ascendia_welcome_seen_v1";
// The residential client-account promo is consumer-facing only. Keep it off B2B /
// account / admin / partner / transactional routes so the message never conflicts
// (e.g. contractors on /for-professionals belong in the Partner Portal, not here).
const HIDDEN_PREFIXES = ["/account", "/admin", "/partners", "/quote", "/portal", "/for-professionals"];

const PERKS = [
  { icon: Home, text: "Save your properties for faster quotes" },
  { icon: FileText, text: "Track every quote, booking & report" },
  { icon: Gift, text: "Earn Ascendia points on every service" },
];

export const ClientWelcomeModal = () => {
  const loc = useLocation();
  const nav = useNavigate();
  const site = useSite();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (HIDDEN_PREFIXES.some((p) => loc.pathname.startsWith(p))) return;
    if (localStorage.getItem(SEEN_KEY)) return;
    let cancelled = false;
    // Only greet logged-out visitors.
    api.get("/client/me")
      .then(() => { /* already a client — never show */ })
      .catch(() => {
        if (cancelled) return;
        const t = setTimeout(() => { if (!cancelled) setOpen(true); }, 3500);
        return () => clearTimeout(t);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = () => { localStorage.setItem(SEEN_KEY, "1"); setOpen(false); };
  const go = (path) => { localStorage.setItem(SEEN_KEY, "1"); setOpen(false); nav(path); };
  const googleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    localStorage.setItem(SEEN_KEY, "1");
    const redirectUrl = window.location.origin + "/account/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="client-welcome-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={dismiss} />
      <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-[color:var(--border-strong)] bg-[#0F0F10] shadow-[var(--shadow-3)]">
        <div className="card-accent-top h-1 w-full" />
        <button onClick={dismiss} data-testid="welcome-dismiss" className="absolute right-3 top-3 h-9 w-9 inline-flex items-center justify-center rounded-lg text-white/60 hover:text-white hover:bg-white/5"><X className="h-4 w-4" /></button>
        <div className="p-7">
          <div className="flex items-center gap-2.5">
            <img src={site.logo} alt="Ascendia Detail" className="h-9 w-9 object-contain" />
            <span className="font-display text-lg font-semibold">Ascendia <span className="text-gold">Detail</span></span>
          </div>
          <h2 className="mt-5 font-display text-2xl font-semibold text-white">Your private client area awaits</h2>
          <p className="mt-2 text-sm text-secondary">Create a free Ascendia account to manage your properties and services like a private client — not a customer.</p>
          <ul className="mt-5 space-y-3">
            {PERKS.map((p, i) => (
              <li key={i} className="flex items-center gap-3 text-sm text-white/85">
                <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gold/10 border border-[color:var(--border-subtle)]"><p.icon className="h-4 w-4 text-gold-soft" /></span>
                {p.text}
              </li>
            ))}
          </ul>
          <button onClick={() => go("/account/signup")} data-testid="welcome-create-account" className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gold px-6 py-3 text-sm font-semibold text-black hover:bg-gold-soft transition-colors">
            <Sparkles className="h-4 w-4" /> Create my free account <ArrowRight className="h-4 w-4" />
          </button>
          <button onClick={googleSignup} data-testid="welcome-google" className="mt-3 w-full inline-flex items-center justify-center gap-3 rounded-xl bg-white px-6 py-3 text-sm font-medium text-[#1f1f1f] hover:bg-white/90 transition-colors">
            <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            Continue with Google
          </button>
          <div className="mt-3 flex items-center justify-center gap-4 text-sm">
            <button onClick={() => go("/account/login")} className="text-gold-soft hover:text-gold" data-testid="welcome-login">I already have one</button>
            <span className="text-white/20">·</span>
            <button onClick={dismiss} className="text-muted-c hover:text-white" data-testid="welcome-maybe-later">Maybe later</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientWelcomeModal;
